<template>
    <div style="position: relative;">
        <ion-list class="ion-no-padding" style="padding-bottom: 65px;">
            <ion-list-header v-show="messageGroups.length">
                <ion-label>
                    <h1>Berichten</h1>
                </ion-label>
            </ion-list-header>

            <ion-item v-for="[date, messages] in messageGroups"
                      :key="date"
                      style="border: 2px solid var(--ion-color-primary, #3880ff); margin-bottom: 10px;"
                      lines="none">
                <ion-list class="ion-no-padding"
                          lines="none"
                          style="width: 100%;">
                    <ion-list-header v-show="messageGroups.length">
                        <ion-label>
                            <h2>{{ date }}</h2>
                        </ion-label>
                    </ion-list-header>

                    <ion-item v-for="message in messages"
                              :key="message.key">
                        <ion-badge slot="start">
                            {{ message.time }}
                        </ion-badge>

                        <ion-label>
                            <h2 v-dompurify-html='message.message.replace(/\n/g, "<br />")'></h2>
                            <p>{{ message.display_name }}</p>
                        </ion-label>

                        <ion-icon slot="end"
                                  class="delete"
                                  size="small"
                                  ios="trash-sharp"
                                  md="trash-sharp"
                                  :title="$t('ncrypt.delete')"
                                  v-if="message.can_delete"
                                  @click="deleteMessage(message.id)"
                                  button/>
                    </ion-item>
                </ion-list>
            </ion-item>

            <ion-infinite-scroll @ionInfinite="load($event)"
                                 position="bottom"
                                 v-bind:class="{ 'infinite-scroll-enabled': ! scrollEndReached }">
                <ion-infinite-scroll-content loadingSpinner="bubbles"
                                             loadingText="Berichten aan het laden..."/>
            </ion-infinite-scroll>
        </ion-list>

        <ion-item :style="{
            position: 'fixed',
            bottom: 0,
            right: 0,
            width: $root.isMobileOrSmall ? '100%' : '50%',
            'box-shadow': '0 2px 1px rgba(0, 0, 0, .16), 0 0 4px 1px rgba(0, 0, 0, .11)',
        }">
            <ion-textarea v-model="newMessage"
                          ref="newMessage"
                          @input="newMessage = $refs.newMessage.value"
                          type="text"
                          :value="newMessage"
                          placeholder="Plaats een bericht"/>

            <ion-button @click="postMessage"
                        :disabled="loading">
                <ion-icon ios='send'
                          md="send"/>
            </ion-button>
        </ion-item>
    </div>
</template>

<script>
    import {ApiError} from "@/errors/ApiError";
    import {Notifier} from "@/services/Notifier";
    import moment from "moment";

    export default {
        data() {
            return {
                messages: [],
                messageGroups: [],
                pagination: null,
                busy: false,
                loading: true,
                infiniteScroll: {},
                newMessage: '',
            }
        },
        computed: {
            scrollEndReached() {
                const lastPage = this.pagination?.meta?.last_page;

                return lastPage && this.getCurrentPage() >= lastPage;
            },
        },
        methods: {
            getCurrentPage() {
                return this.pagination?.meta?.current_page || 0;
            },
            async load(event) {
                if (this.scrollEndReached || this.busy) {
                    if (event) {
                        event.target.complete();
                    }
                    this.loading = false;
                    return;
                }

                this.loading = true;
                this.busy = true;

                this.$http.get(`/message-board?page=${this.getCurrentPage() + 1}`)
                    .then(async ({data: paginator}) => {
                        this.pagination = paginator;

                        this.messages = this.messages.concat(paginator.data)

                        this.renderMessages();

                        if (event) {
                            event.target.complete();
                        }
                    })
                    .catch(reason => new ApiError(reason))
                    .finally(() => {
                        this.busy = false;
                        this.loading = false;
                    });
            },
            postMessage() {
                if (this.loading) {
                    return;
                }

                if (! this.newMessage) {
                    Notifier.showToast({
                        header: 'Error',
                        message: 'Vul eerst een bericht in',
                        color: 'danger',
                        buttons: ['OK'],
                    });
                    return;
                }

                this.loading = true;
                this.busy = true;

                this.$http.put(`/message-board/message/create`, {
                    message: this.newMessage,
                })
                    .then(async ({data}) => {
                        if (! data || data.success === false) {
                            new ApiError(data);
                            return;
                        }

                        this.newMessage = '';

                        this.pagination = data;

                        this.messages = this.pagination.data

                        this.renderMessages();
                    })
                    .catch(reason => new ApiError(reason))
                    .finally(() => {
                        this.busy = false;
                        this.loading = false;
                    });
            },
            deleteMessage(id) {
                this.loading = true;
                this.busy = true;

                this.$http.delete(`/message-board/message/${id}/delete`)
                    .then(async ({data}) => {
                        if (! data || data.success === false) {
                            new ApiError(data);
                            return;
                        }

                        this.pagination = data;

                        this.messages = this.pagination.data

                        this.renderMessages();
                    })
                    .catch(reason => new ApiError(reason))
                    .finally(() => {
                        this.busy = false;
                        this.loading = false;
                    });
            },
            renderMessages() {
                const map = new Map();

                this.messages.forEach(message => {
                    const key = message.day;

                    const collection = map.get(key);
                    if (! collection) {
                        map.set(key, [message]);
                    } else {
                        collection.push(message);
                    }
                })

                this.messageGroups = Array.from(map).map(([key, values]) => {
                    return [
                        moment(key, 'DD-MM-YYYY').format('dddd, D MMMM'),
                        values,
                    ];
                });
            },
        },
        mounted() {
            this.infiniteScroll = document.getElementById('infinite-scroll');

            this.load();
        },
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
    .message {
        padding: 8px 0;
    }

    ion-list-header {
        ion-label::first-letter {
            text-transform: capitalize;
        }
    }

    ion-label {
        h2 {
            white-space: pre-wrap;
        }
    }
</style>
